import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { Tooltip } from '../ui';
import { formatMoneyShort, LOT_VALUE_TIPS } from '../../utils/lotValue';
import { buildAiValueView, gapScaleOf, inventoryLink } from '../../utils/branchView';
import {
    AI_VALUE_EMPTY, GAP_PCT_TIP, GAP_TIP, SELECTION_LABEL, compareRows, formatGapMoney, formatGapPct,
    severityClass, usedUnitsTip,
} from '../BranchOverview/BranchAiValueTable';
import { GapBar } from './ComparisonTable';
import { useOpenInventory } from './InventoryLink';

const SORT_LABELS = { gapTotal: 'Gap $', gapPct: 'Gap %' };
// Today's colour scale for this table: amber / red above the thresholds, the
// rest neutral (BranchAiValueTable severityClass).
const TONE = { warn: 'warn', critical: 'crit', neutral: 'neutral' };

const subtitle = (allSelected, count, single, benchLabel, benchPhrase) => {
    if (allSelected) return 'All branches, used units only. Click a branch to open its inventory.';
    const phrase = benchPhrase || (benchLabel ? benchLabel.replace(/ \(\d+\)$/, '').toLowerCase() : null);
    const also = phrase ? ` and ${phrase}` : '';
    if (count === 1) return `${single} against the dealership${also}, used units only. Click to open its inventory.`;
    return `Selected branches, their total, and the dealership${also} for reference. Used units only.`;
};

function SummaryRow({
    summary, label, muted, scale, testId,
}) {
    const { gapThresholds } = useDealershipSettings();
    const tone = muted ? 'ref' : TONE[severityClass(summary.gapPct, gapThresholds)];
    return (
        <tr className={muted ? 'br2-ref' : 'br2-total'} data-testid={testId}>
            <td className="br2-bav-b">{label}</td>
            <td className="v2-r br2-num">{summary.pricedCount == null ? '—' : summary.pricedCount.toLocaleString('en-US')}</td>
            <td className="v2-r br2-num">{formatMoneyShort(summary.askingTotal)}</td>
            <td className="v2-r br2-num">{formatMoneyShort(summary.aiTotal)}</td>
            <td className={`v2-r br2-num br2-ink-${muted ? 'muted' : tone}`}>{formatGapMoney(summary.gapTotal)}</td>
            <td className="v2-r">
                <span className="br2-gp">
                    <GapBar gap={summary.gapPct} scale={scale} tone={muted ? 'ref' : tone} />
                    <span className={`br2-gp-v br2-ink-${muted ? 'muted' : tone}`}>{formatGapPct(summary.gapPct)}</span>
                </span>
            </td>
        </tr>
    );
}

SummaryRow.propTypes = {
    summary: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
    muted: PropTypes.bool,
    scale: PropTypes.number.isRequired,
    testId: PropTypes.string,
};

/**
 * AI value by location (P-BR-055..071) in the v2 look, with today's
 * behaviour: used units only; all selected → every priced branch and the
 * Dealership total; partial → the selected branches, a Selection total for
 * two or more and the Dealership for reference. Sort on Gap $ / Gap %
 * (Gap $ high→low first), rows without a value last. A row opens that
 * branch's inventory and jumps to the top. Tooltips are focusable. In compare
 * mode with another basis, `benchmark` ({ label, summary, phrase }) adds that
 * benchmark's row under the Dealership row (the median branch has a gap %
 * only); `phrase` names it in the subtitle (else the label, lower-cased).
 */
export default function AiValueTableV2({
    units, loading, selectedBranches, allSelected, selectionCount, allNames, benchmark,
}) {
    const { gapThresholds } = useDealershipSettings();
    const [sort, setSort] = useState({ key: 'gapTotal', dir: 'desc' });
    const open = useOpenInventory();
    const view = useMemo(
        () => buildAiValueView(units, selectedBranches, allSelected),
        [units, selectedBranches, allSelected],
    );
    const { rows, selection, dealership, usedUnits, showAll } = view;
    const bench = !allSelected && benchmark ? benchmark : null;
    const scale = gapScaleOf([...rows, selection, dealership, bench?.summary]);
    const sorted = useMemo(() => [...rows].sort(compareRows(sort.key, sort.dir)), [rows, sort]);
    const single = selectionCount === 1 ? [...selectedBranches][0] : null;

    const onSort = (key) => setSort((prev) => (prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }));
    const openBranch = (branch) => open(inventoryLink({ names: [branch], allNames }));

    const sortHeader = (key, tip) => {
        const on = sort.key === key;
        return (
            <th scope="col" className="v2-r" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                <span className="v2-th-split">
                    <button type="button" onClick={() => onSort(key)}>
                        {SORT_LABELS[key]}
                        <span className="v2-ar" aria-hidden="true">{on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                    </button>
                    <Tooltip label={`About ${SORT_LABELS[key]}`} content={tip} />
                </span>
            </th>
        );
    };
    const tipHeader = (label, tip) => (
        <th scope="col" className="v2-r">
            <span className="v2-th-split">
                <span className="br2-th-plain">{label}</span>
                <Tooltip label={`About ${label}`} content={tip} />
            </span>
        </th>
    );

    let body;
    if (loading && usedUnits.length === 0) {
        body = <p className="br2-bav-empty">{AI_VALUE_EMPTY.loading}</p>;
    } else if (dealership.pricedCount === 0) {
        body = <p className="br2-bav-empty">{AI_VALUE_EMPTY.dealership}</p>;
    } else if (rows.length === 0) {
        body = <p className="br2-bav-empty">{AI_VALUE_EMPTY.selection}</p>;
    } else {
        body = (
            <div className="v2-tscroll br2-tscroll">
                <table className="v2-table br2-bav" aria-label="AI value by location">
                    <thead>
                        <tr>
                            <th scope="col"><span className="br2-th-plain br2-th-pad">Branch</span></th>
                            {tipHeader('Used units', usedUnitsTip(view.scopePriced, view.scopeUsed))}
                            {tipHeader('Asking', LOT_VALUE_TIPS.asking)}
                            {tipHeader('AI value', LOT_VALUE_TIPS.ai)}
                            {sortHeader('gapTotal', GAP_TIP)}
                            {sortHeader('gapPct', GAP_PCT_TIP)}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => {
                            const tone = TONE[severityClass(r.gapPct, gapThresholds)];
                            return (
                                <tr
                                    key={r.branch}
                                    className="br2-tr"
                                    title={`View ${r.branch} inventory`}
                                    onClick={() => openBranch(r.branch)}
                                >
                                    <td className="br2-bav-b">
                                        <button
                                            type="button"
                                            className="br2-bav-btn"
                                            aria-label={`Open ${r.branch} inventory`}
                                            onClick={(e) => { e.stopPropagation(); openBranch(r.branch); }}
                                        >
                                            {r.branch}
                                        </button>
                                    </td>
                                    <td className="v2-r br2-num">{r.pricedCount.toLocaleString('en-US')}</td>
                                    <td className="v2-r br2-num">{formatMoneyShort(r.askingTotal)}</td>
                                    <td className="v2-r br2-num">{formatMoneyShort(r.aiTotal)}</td>
                                    <td className={`v2-r br2-num br2-ink-${tone}`}>{formatGapMoney(r.gapTotal)}</td>
                                    <td className="v2-r">
                                        <span className="br2-gp">
                                            <GapBar gap={r.gapPct} scale={scale} tone={tone} />
                                            <span className={`br2-gp-v br2-ink-${tone}`}>{formatGapPct(r.gapPct)}</span>
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                        {selection && <SummaryRow summary={selection} label={SELECTION_LABEL} scale={scale} />}
                        <SummaryRow summary={dealership} label={dealership.branch} muted={!showAll} scale={scale} />
                        {bench && <SummaryRow summary={bench.summary} label={bench.label} muted scale={scale} testId="bav-bench" />}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <section className="v2-board br2-board" aria-labelledby="br2-bav-h">
            <div className="br2-card-h">
                <h2 id="br2-bav-h">AI value by location</h2>
                <span className="br2-sub">{subtitle(allSelected, selectionCount, single, bench?.label, bench?.phrase)}</span>
            </div>
            {body}
        </section>
    );
}

AiValueTableV2.propTypes = {
    units: PropTypes.arrayOf(PropTypes.object),
    loading: PropTypes.bool,
    selectedBranches: PropTypes.instanceOf(Set).isRequired,
    allSelected: PropTypes.bool.isRequired,
    selectionCount: PropTypes.number.isRequired,
    allNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    benchmark: PropTypes.shape({ label: PropTypes.string, summary: PropTypes.object, phrase: PropTypes.string }),
};

AiValueTableV2.defaultProps = { units: [], loading: false, benchmark: null };
